import qs from 'query-string'
import {
    DataGridPayload,
    ResponseArray,
} from 'vnetwork-ui/dist/components/GokuDataGrid'
import { ResponseObject } from '@/types/shared'
import automation from '@/utils/axios/automation'

import {
    CreateReportPayload,
    ResendType,
    UpdateReportPayload,
    ViewReportType,
} from '../types/payload'
import {
    ReportWAF,
    ReportWAFTask,
    PipelineStats,
    TaskWithCustomer,
    ViewReportResponse,
} from '../types/response'

// ============================================
// Transform helpers: API → Frontend format
// ============================================

const transformReport = (raw: any): ReportWAF => {
    if (!raw) return {} as ReportWAF
    return {
        id: raw.report_uuid,
        report_name: raw.report_name || '',
        // `provider` + `report_engine` come from the joined
        // report_attributes row (backend serialise lifts them into the
        // top-level report payload via LEFT JOIN on report_uuid).
        provider: raw.provider || undefined,
        report_engine: raw.report_engine || undefined,
        customer_id: raw.report_referrer_id,
        customer_name: raw.customer_name || raw.report_referrer_name || '',
        description: raw.report_description,
        priority: raw.report_priority,
        send_format: raw.report_scope,
        send_frequency: raw.report_interval,
        status: raw.report_status === 'active' ? 'activate' : 'deactivate',
        service_customer_id:
            raw.service_customer_id || raw.provider_customer_id || null,
        // Bound datacentral service — needed so the Update form + List
        // pre-select/badge the current binding on read. Without mapping
        // it here the value was dropped and the form showed "Unbound"
        // even after a successful bind.
        service_id: raw.service_id ?? null,
        domain_source: raw.domain_source || '',
        domains: raw.domains || [],
        registration_date: raw.created_at,
        // Real last-sent time = backend `last_send_date` (MAX completed
        // task time from batch_last_send_dates), NOT the report row's
        // `updated_at` (that changes on any edit and is unrelated to
        // delivery). Null when the report has never sent.
        last_send_date: raw.last_send_date ?? null,
        send_status: raw.report_status,
        created_at: raw.created_at,
        updated_at: raw.updated_at,
    }
}

const parseJidDates = (jid: string) => {
    // format: {report_id}_{hash}_{YYYYMMDDHHMMSS}_{YYYYMMDDHHMMSS}
    const parts = jid?.split('_') || []
    const gteRaw = parts[2] // e.g. 20250901000000
    const lteRaw = parts[3] // e.g. 20250930235959
    const fmt = (s: string) =>
        s
            ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}T${s.slice(8, 10)}:${s.slice(10, 12)}:${s.slice(12, 14)}Z`
            : null
    return { gte: fmt(gteRaw), lte: fmt(lteRaw) }
}

const transformTask = (raw: any): ReportWAFTask => {
    const dates = parseJidDates(raw.task_referrer_jid)
    return {
        id: raw.task_id,
        report_id: parseInt(raw.task_referrer_jid?.split('_')?.[0]) || 0,
        report_type: raw.task_bind?.replace('waf::report::', '') || '',
        file_path: raw.download_url || null,
        file_status: raw.file_status || raw.task_status,
        send_status: raw.task_status,
        completion_date: raw.task_completed_at,
        report_name: raw.task_name,
        created_at: raw.task_created_at,
        gte: dates.gte,
        lte: dates.lte,
        specific: raw.task_description || null,
        task_referrer_jid: raw.task_referrer_jid || null,
    }
}

const transformPipelineTask = (raw: any): TaskWithCustomer => ({
    id: raw.task_id,
    report_id: parseInt(raw.task_referrer_jid?.split('_')?.[0]) || 0,
    customer_name: raw.customer_name || '',
    customer_id: raw.customer_id || 0,
    report_type: raw.task_bind?.replace('waf::report::', '') || '',
    report_name: raw.report_name || raw.task_name || null,
    send_status: raw.task_status,
    error_message: raw.metadata?.error || null,
    error_step: null,
    file_path: raw.metadata?.file_path || null,
    duration_sec: raw.duration_sec || 0,
    created_at: raw.task_created_at,
})

// ============================================
// Report CRUD
// ============================================

export const listReportWaf = async (
    payload: DataGridPayload
): Promise<ResponseArray<ReportWAF>> => {
    const response = await automation.get<{
        data: { reports: any[]; pagination: any }
    }>(`/workers/reports?${qs.stringify(payload)}`)

    const { reports, pagination } = response.data.data
    return {
        results: reports.map(transformReport),
        pagination: {
            total: pagination?.total_items || pagination?.total || 0,
            page: pagination?.page || 1,
            page_size: pagination?.page_size || 20,
            total_pages: pagination?.total_pages || 1,
            page_next: (pagination?.page || 1) + 1,
            page_prev: (pagination?.page || 1) - 1,
        },
    }
}

export const createReportWaf = async (
    payload: CreateReportPayload
): Promise<ResponseObject<ReportWAF>> => {
    // Controller (api/workers/watch/reports_controller.rb#create) expects
    // the body nested as `{ report: { report: {...}, report_attribute: {...} } }`
    // — it splits the two halves into `Report.create!` and
    // `ReportAttribute.create!`. The old flat `{ report: payload }` shape
    // blew up when the controller tried `params[:report][:report]`.
    const response = await automation.post<{ data: any }>('/workers/reports', {
        report: {
            report: {
                report_name: payload.customer_name,
                report_description: payload.description,
                report_priority: payload.priority,
                report_scope: payload.send_format,
                report_interval: payload.send_frequency,
                report_status:
                    payload.status === 'activate' ? 'active' : 'inactive',
                report_type: 'waf',
                report_referrer_id: payload.customer_id,
                // Bind to a datacentral service (nullable) — worker uses it
                // to pick the correct service.report mailer group.
                service_id: payload.service_id ?? null,
            },
            report_attribute: {
                provider: payload.provider,
                report_engine: payload.report_engine,
                provider_customer_id: payload.service_customer_id,
                domain_source: payload.domain_source,
                domains: payload.domains,
            },
        },
    })
    return { message: '', result: transformReport(response.data.data.report) }
}

export const updateReportWaf = async (
    id: string,
    payload: UpdateReportPayload
): Promise<ResponseObject<ReportWAF>> => {
    const response = await automation.patch<{ data: any }>(
        `/workers/reports/${id}/update_report_waf`,
        {
            data: {
                report: {
                    report_name: payload.report_name,
                    report_priority: payload.priority,
                    report_scope: payload.send_format,
                    // Bind the report to a datacentral service so the worker
                    // resolves the right service.report mailer at send time.
                    // Without this the update_report_waf payload dropped it
                    // and the binding never persisted.
                    service_id: payload.service_id ?? null,
                },
                report_attribute: {
                    domains: payload.domains,
                    domain_source: payload.domain_source,
                    provider_customer_id: payload.service_customer_id,
                    provider: payload.provider,
                    report_engine: payload.report_engine,
                },
            },
        }
    )
    return { message: '', result: transformReport(response.data.data.report) }
}

export const getReportWaf = async (
    id: string
): Promise<ResponseObject<ReportWAF>> => {
    const response = await automation.get<{ data: { report: any } }>(
        `/workers/reports/${id}`
    )
    return { message: '', result: transformReport(response.data.data.report) }
}

export const deleteReportWaf = async (
    id: string
): Promise<ResponseObject<ReportWAF>> => {
    const response = await automation.delete<{ data: { report: any } }>(
        `/workers/reports/${id}`
    )
    return { message: '', result: transformReport(response.data.data.report) }
}

// ============================================
// Report Actions
// ============================================

export const toggleReportWafStatus = async (
    id: string,
    _status: 'activate' | 'deactivate'
): Promise<ResponseObject<any>> => {
    const response = await automation.post<{ data: { report: any } }>(
        `/workers/reports/${id}/toggle_status`
    )
    return { message: '', result: transformReport(response.data.data.report) }
}

export const resendReport = async (
    payload: ResendType
): Promise<ResponseObject<any>> => {
    // Controller (watch/reports_controller.rb#resend) reads gte/lte/type
    // from params[:report]. `mode: resend_last` means the caller is
    // replaying the period from the report's last completed task — UI
    // should have already resolved gte/lte before calling this, so the
    // body shape is the same for both modes.
    const response = await automation.post<{ data: any }>(
        `/workers/reports/${payload.id}/resend`,
        {
            report: {
                gte: payload.gte,
                lte: payload.lte,
                type: payload.type,
            },
        }
    )
    return { message: '', result: response.data.data }
}

export const viewReport = async (
    payload: ViewReportType
): Promise<ViewReportResponse> => {
    return {
        success: true,
        preview_url: `${import.meta.env.APP_REPORT_URL}/${payload.id}${payload.start_date && payload.end_date ? `?gte=${payload.start_date}&lte=${payload.end_date}` : ''}`,
        report_id: Number(payload.id),
        start_date: payload.start_date ?? '',
        end_date: payload.end_date ?? '',
    }
}

// Fetch the cached manifest for a completed task. Hits the @v2 Go
// service directly (GET /api/v2/reports/cached/{specific}/{key}) which
// is the owner of S3 layout. The endpoint is engine-agnostic — both v1
// and v2 manifests live under `reports/{specific}/{key}.json`. We only
// need the generator's `uuid` field to build the snapshot-view URL
// (report.vnetwork.dev/v{engine}/reports/{uuid}).
export const getCachedManifest = async (
    specific: string,
    key: string
): Promise<{ uuid: string | null }> => {
    const base = import.meta.env.APP_REPORT_URL
    const url = `${base}/api/v2/reports/cached/${encodeURIComponent(specific)}/${encodeURIComponent(key)}`
    const response = await fetch(url, { cache: 'no-store' })
    if (!response.ok) {
        throw new Error(`cached manifest fetch failed: ${response.status}`)
    }
    const manifest = await response.json()
    return { uuid: manifest?.uuid || null }
}

// ============================================
// Report History & Tasks
// ============================================

export const getReportHistory = async (
    payload: DataGridPayload,
    reportId: string
): Promise<ResponseArray<ReportWAFTask>> => {
    const response = await automation.get<{
        data: { tasks: any[]; pagination: any }
    }>(`/workers/reports/${reportId}/histories?${qs.stringify(payload)}`)

    const { tasks, pagination } = response.data.data
    return {
        results: (tasks || []).map(transformTask),
        pagination: {
            total: pagination?.total_items || pagination?.total || 0,
            page: pagination?.page || 1,
            page_size: pagination?.page_size || 20,
            total_pages: pagination?.total_pages || 1,
            page_next: (pagination?.page || 1) + 1,
            page_prev: (pagination?.page || 1) - 1,
        },
    }
}

export const downloadReport = async (
    reportUuid: string,
    taskId: number
): Promise<void> => {
    const status = await getTaskStatus(reportUuid, taskId)
    if (!status.download_url) {
        throw new Error('PDF not available')
    }

    const response = await fetch(status.download_url)
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `report_${reportUuid}_${taskId}.pdf`
    link.click()
    URL.revokeObjectURL(url)
}

export interface TaskStatus {
    task_id: number
    task_status: string
    file_status: 'generating' | 'ready' | 'error'
    file_size: number | null
    download_url: string | null
}

export const getTaskStatus = async (
    reportUuid: string,
    taskId: number
): Promise<TaskStatus> => {
    const response = await automation.get<{ data: TaskStatus }>(
        `/workers/reports/${reportUuid}/tasks/${taskId}/status`
    )
    return response.data.data
}

// ============================================
// Pipeline Dashboard APIs
// ============================================

export const getPipelineStats = async (): Promise<PipelineStats> => {
    const response = await automation.get<{ data: PipelineStats }>(
        '/workers/reports/pipeline_stats'
    )
    return response.data.data
}

export const getPipelineTasks = async (
    payload: DataGridPayload
): Promise<ResponseArray<TaskWithCustomer>> => {
    const response = await automation.get<{
        data: { tasks: any[]; pagination: any }
    }>(`/workers/reports/pipeline_tasks?${qs.stringify(payload)}`)

    const { tasks, pagination } = response.data.data
    return {
        results: tasks.map(transformPipelineTask),
        pagination: {
            total: pagination?.total_items || pagination?.total || 0,
            page: pagination?.page || 1,
            page_size: pagination?.page_size || 20,
            total_pages: pagination?.total_pages || 1,
            page_next: (pagination?.page || 1) + 1,
            page_prev: (pagination?.page || 1) - 1,
        },
    }
}

export const retryTask = async (
    taskId: number
): Promise<{ success: boolean; message: string }> => {
    const response = await automation.post<{ data: { message: string } }>(
        `/workers/reports/pipeline_tasks/${taskId}/retry`
    )
    return { success: true, message: response.data.data.message }
}

// ============================================
// Provider Customer Search APIs
// ============================================

import type { MlyticsCustomer } from '../types/payload'

export const searchMlyticsCustomers = async (
    keyword: string
): Promise<MlyticsCustomer[]> => {
    const response = await automation.get<{ data: MlyticsCustomer[] }>(
        `/workers/reports/providers/mlytics/customers?q=${encodeURIComponent(keyword)}`
    )
    return response.data.data || []
}

export const searchMaxiSafeCustomers = async (
    keyword: string
): Promise<MlyticsCustomer[]> => {
    const response = await automation.get<{ data: MlyticsCustomer[] }>(
        `/workers/reports/providers/maxisafe/customers?q=${encodeURIComponent(keyword)}`
    )
    return response.data.data || []
}
